import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Tab } from '@headlessui/react';
import {
  XMarkIcon, PencilIcon, SparklesIcon, CheckCircleIcon, PlusIcon,
  PhoneIcon, EnvelopeIcon, GlobeAltIcon, BuildingOfficeIcon,
  UserIcon, ChatBubbleLeftIcon, ClipboardDocumentListIcon,
  ArrowRightCircleIcon, BanknotesIcon, CalendarDaysIcon,
  ExclamationCircleIcon, TrashIcon, ChevronRightIcon, MapPinIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { toast } from 'react-hot-toast';
import { STAGE_CONFIG, STAGE_LABELS } from '../../lib/validation';
import type { ProspectStage } from '../../lib/validation';
import ProspectTaskSheet from './ProspectTaskSheet';
import ProspectContactSheet from './ProspectContactSheet';
import ProspectCommitmentSheet from './ProspectCommitmentSheet';
import ProspectActivateModal from './ProspectActivateModal';

interface ProspectContact {
  id: string;
  full_name: string;
  title?: string;
  email?: string;
  phone?: string;
  preferred_contact_method?: string;
  linkedin_url?: string;
  is_primary: boolean;
  is_official_contact?: boolean;
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  notes?: string;
}

interface ProspectTask {
  id: string;
  task_type: string;
  title: string;
  due_date?: string;
  priority: string;
  is_completed: boolean;
  completed_at?: string;
  notes?: string;
}

interface ProspectCommitment {
  id: string;
  tier_interest?: string;
  amount_pledged?: number;
  expected_close?: string;
  probability_pct?: number;
  status: string;
  notes?: string;
  created_at: string;
}

interface ProspectActivity {
  id: string;
  activity_type: string;
  title: string;
  body?: string;
  metadata?: Record<string, unknown>;
  performed_by?: string;
  created_at: string;
}

interface ProspectFull {
  id: string;
  company_name: string;
  industry?: string;
  company_size?: string;
  website?: string;
  stage: string;
  source?: string;
  source_detail?: string;
  notes?: string;
  lost_reason?: string;
  partner_id?: string;
  tournament_id?: string;
  logo_url?: string;
  prospect_contacts: ProspectContact[];
  prospect_tasks: ProspectTask[];
  prospect_commitments: ProspectCommitment[];
  prospect_activities: ProspectActivity[];
}

interface ProspectDetailsDrawerProps {
  prospectId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  onEdit: (prospect: ProspectFull) => void;
}

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-gray-100 text-gray-600',
  medium: 'bg-blue-50 text-blue-700',
  high: 'bg-orange-50 text-orange-700',
  urgent: 'bg-red-50 text-red-600',
};

const ACTIVITY_ICONS: Record<string, React.ElementType> = {
  stage_change:        ArrowRightCircleIcon,
  note_added:          ChatBubbleLeftIcon,
  contact_added:       UserIcon,
  task_created:        ClipboardDocumentListIcon,
  task_completed:      CheckCircleIcon,
  commitment_added:    BanknotesIcon,
  email_sent:          EnvelopeIcon,
  call_logged:         PhoneIcon,
  meeting_logged:      CalendarDaysIcon,
  prospect_created:    BuildingOfficeIcon,
  prospect_activated:  SparklesIcon,
};

const COMMITMENT_STATUS_COLORS: Record<string, string> = {
  draft:     'bg-gray-100 text-gray-600',
  verbal:    'bg-blue-50 text-blue-700',
  written:   'bg-yellow-50 text-yellow-700',
  signed:    'bg-emerald-50 text-emerald-700',
  cancelled: 'bg-red-50 text-red-600',
};

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function isOverdue(dueDate?: string) {
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
}

export default function ProspectDetailsDrawer({ prospectId, isOpen, onClose, onRefresh, onEdit }: ProspectDetailsDrawerProps) {
  const [prospect, setProspect] = useState<ProspectFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [taskSheet, setTaskSheet] = useState<{ open: boolean; task?: ProspectTask | null }>({ open: false });
  const [contactSheet, setContactSheet] = useState<{ open: boolean; contact?: ProspectContact | null }>({ open: false });
  const [commitmentSheet, setCommitmentSheet] = useState<{ open: boolean; commitment?: ProspectCommitment | null }>({ open: false });
  const [activateOpen, setActivateOpen] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState<string | null>(null);

  const fetchProspect = useCallback(async () => {
    if (!prospectId) return;
    setLoading(true);
    const { data } = await supabase
      .from('prospects')
      .select(`*, prospect_contacts(*), prospect_tasks(*), prospect_commitments(*), prospect_activities(*)`)
      .eq('id', prospectId)
      .maybeSingle();
    if (data) setProspect(data as ProspectFull);
    setLoading(false);
  }, [prospectId]);

  useEffect(() => {
    if (isOpen && prospectId) fetchProspect();
  }, [isOpen, prospectId, fetchProspect]);

  const handleStageChange = async (newStage: ProspectStage) => {
    if (!prospect || prospect.stage === newStage) return;
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('prospects').update({ stage: newStage }).eq('id', prospect.id);
    await supabase.from('prospect_activities').insert([{
      prospect_id: prospect.id,
      activity_type: 'stage_change',
      title: `Stage changed to ${STAGE_LABELS[newStage]}`,
      metadata: { from_stage: prospect.stage, to_stage: newStage },
      performed_by: user?.id,
    }]);
    toast.success(`Moved to ${STAGE_LABELS[newStage]}`);
    await fetchProspect();
    onRefresh();
  };

  const handleTaskToggle = async (task: ProspectTask) => {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('prospect_tasks').update({
      is_completed: !task.is_completed,
      completed_at: !task.is_completed ? new Date().toISOString() : null,
    }).eq('id', task.id);
    if (!task.is_completed) {
      await supabase.from('prospect_activities').insert([{
        prospect_id: prospect!.id,
        activity_type: 'task_completed',
        title: `Task completed: ${task.title}`,
        metadata: { task_id: task.id },
        performed_by: user?.id,
      }]);
    }
    await fetchProspect();
  };

  const handleDeleteContact = async (contactId: string) => {
    await supabase.from('prospect_contacts').delete().eq('id', contactId);
    setDeletingContactId(null);
    toast.success('Contact removed');
    await fetchProspect();
  };

  const handleAddNote = async () => {
    if (!noteText.trim() || !prospect) return;
    setAddingNote(true);
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from('prospect_activities').insert([{
      prospect_id: prospect.id,
      activity_type: 'note_added',
      title: 'Note added',
      body: noteText.trim(),
      performed_by: user?.id,
    }]);
    setNoteText('');
    await fetchProspect();
    setAddingNote(false);
  };

  const stages = Object.keys(STAGE_CONFIG) as ProspectStage[];
  const currentStageIdx = prospect ? stages.indexOf(prospect.stage as ProspectStage) : -1;
  const prevStage = currentStageIdx > 0 ? stages[currentStageIdx - 1] : null;
  const nextStage = currentStageIdx >= 0 && currentStageIdx < stages.length - 1 ? stages[currentStageIdx + 1] : null;

  const activeTasks = prospect?.prospect_tasks.filter(t => !t.is_completed) || [];
  const completedTasks = prospect?.prospect_tasks.filter(t => t.is_completed) || [];
  const overdueTasks = activeTasks.filter(t => isOverdue(t.due_date));
  const sortedActivities = [...(prospect?.prospect_activities || [])].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  const latestCommitment = prospect?.prospect_commitments?.[0];

  const stageConfig = prospect ? STAGE_CONFIG[prospect.stage as ProspectStage] : null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />
            <motion.div
              className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white shadow-2xl flex flex-col"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            >
              {loading && !prospect ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-golf-orange border-t-transparent rounded-full animate-spin" />
                </div>
              ) : prospect ? (
                <>
                  <div className="bg-golf-gray px-5 py-4 flex-shrink-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h2 className="text-white font-bold text-lg leading-tight truncate">{prospect.company_name}</h2>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          {stageConfig && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${stageConfig.color}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${stageConfig.dot}`} />
                              {stageConfig.label}
                            </span>
                          )}
                          {prospect.industry && <span className="text-gray-400 text-xs">{prospect.industry}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {prospect.stage !== 'won' && prospect.stage !== 'lost' && (
                          <button onClick={() => setActivateOpen(true)} className="flex items-center gap-1.5 px-3 py-1.5 bg-golf-orange text-white text-xs font-medium rounded-lg hover:bg-golf-orange/90 transition-colors">
                            <SparklesIcon className="w-3.5 h-3.5" />
                            Activate
                          </button>
                        )}
                        <button onClick={() => onEdit(prospect)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                          <PencilIcon className="w-4 h-4 text-gray-300" />
                        </button>
                        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors">
                          <XMarkIcon className="w-4 h-4 text-gray-300" />
                        </button>
                      </div>
                    </div>

                    {prospect.stage !== 'won' && prospect.stage !== 'lost' && (
                      <div className="flex gap-2 mt-3">
                        {prevStage && (
                          <button onClick={() => handleStageChange(prevStage)} className="flex items-center gap-1 px-2 py-1 bg-white/10 text-gray-300 text-xs rounded-lg hover:bg-white/20 transition-colors">
                            ← {STAGE_LABELS[prevStage]}
                          </button>
                        )}
                        {nextStage && (
                          <button onClick={() => handleStageChange(nextStage)} className="flex items-center gap-1 px-2 py-1 bg-white/10 text-gray-300 text-xs rounded-lg hover:bg-white/20 transition-colors ml-auto">
                            {STAGE_LABELS[nextStage]} <ChevronRightIcon className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <Tab.Group>
                    <Tab.List className="flex border-b border-gray-200 bg-gray-50 flex-shrink-0 px-2">
                      {['Overview', 'Contacts', 'Tasks', 'Commitment', 'Timeline'].map(tab => (
                        <Tab key={tab} className={({ selected }) =>
                          `px-4 py-3 text-sm font-medium border-b-2 transition-colors focus:outline-none whitespace-nowrap ${
                            selected ? 'border-golf-orange text-golf-orange' : 'border-transparent text-gray-500 hover:text-gray-700'
                          }`
                        }>
                          {tab === 'Tasks' && overdueTasks.length > 0 ? (
                            <span className="flex items-center gap-1">{tab} <span className="w-4 h-4 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">{overdueTasks.length}</span></span>
                          ) : tab}
                        </Tab>
                      ))}
                    </Tab.List>

                    <Tab.Panels className="flex-1 overflow-y-auto">
                      <Tab.Panel className="p-5 space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          {prospect.website && (
                            <a href={prospect.website} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline col-span-2">
                              <GlobeAltIcon className="w-4 h-4 flex-shrink-0" />
                              {prospect.website}
                            </a>
                          )}
                          {prospect.company_size && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Size</p>
                              <p className="text-sm font-medium text-golf-gray capitalize">{prospect.company_size}</p>
                            </div>
                          )}
                          {prospect.source && (
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Source</p>
                              <p className="text-sm font-medium text-golf-gray capitalize">{prospect.source.replace('_', ' ')}</p>
                            </div>
                          )}
                          {prospect.source_detail && (
                            <div className="col-span-2">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Source Detail</p>
                              <p className="text-sm text-golf-gray">{prospect.source_detail}</p>
                            </div>
                          )}
                        </div>

                        {prospect.notes && (
                          <div className="bg-golf-sand/40 rounded-xl p-3">
                            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Notes</p>
                            <p className="text-sm text-golf-gray whitespace-pre-wrap">{prospect.notes}</p>
                          </div>
                        )}

                        {prospect.partner_id && (
                          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                            <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-emerald-800">Activated as Partner</p>
                              <p className="text-xs text-emerald-600">Confirmed partner record created</p>
                            </div>
                          </div>
                        )}

                        {prospect.prospect_contacts.filter(c => c.is_primary).map(c => (
                          <div key={c.id} className="border border-gray-200 rounded-xl p-3 space-y-2">
                            <div className="flex items-center gap-2">
                              <div className="w-7 h-7 rounded-full bg-golf-orange/10 flex items-center justify-center flex-shrink-0">
                                <UserIcon className="w-4 h-4 text-golf-orange" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-sm font-medium text-golf-gray">{c.full_name}</p>
                                  {c.is_official_contact && (
                                    <span className="inline-flex items-center gap-0.5 text-xs bg-golf-gray/10 text-golf-gray px-1.5 py-0.5 rounded-full">
                                      <ShieldCheckIcon className="w-3 h-3" />Official
                                    </span>
                                  )}
                                </div>
                                {c.title && <p className="text-xs text-gray-500">{c.title}</p>}
                              </div>
                              <span className="flex-shrink-0 text-xs bg-golf-orange/10 text-golf-orange px-2 py-0.5 rounded-full">Primary</span>
                            </div>
                            <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                              {c.email && <a href={`mailto:${c.email}`} className="hover:text-golf-orange flex items-center gap-1"><EnvelopeIcon className="w-3 h-3" />{c.email}</a>}
                              {c.phone && <a href={`tel:${c.phone}`} className="hover:text-golf-orange flex items-center gap-1"><PhoneIcon className="w-3 h-3" />{c.phone}</a>}
                            </div>
                            {(c.street || c.city || c.state) && (
                              <div className="flex items-start gap-1 text-xs text-gray-400">
                                <MapPinIcon className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                <span>{[c.street, c.city, c.state, c.postal_code, c.country].filter(Boolean).join(', ')}</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </Tab.Panel>

                      <Tab.Panel className="p-5 space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-golf-gray">Contacts ({prospect.prospect_contacts.length})</h3>
                          <button onClick={() => setContactSheet({ open: true })} className="flex items-center gap-1.5 px-3 py-1.5 bg-golf-orange text-white text-xs font-medium rounded-lg hover:bg-golf-orange/90 transition-colors">
                            <PlusIcon className="w-3.5 h-3.5" />
                            Add Contact
                          </button>
                        </div>

                        {prospect.prospect_contacts.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            <UserIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No contacts yet</p>
                          </div>
                        ) : (
                          prospect.prospect_contacts.map(c => (
                            <div key={c.id} className="border border-gray-200 rounded-xl p-4">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs font-semibold text-gray-600">{c.full_name.charAt(0)}</span>
                                  </div>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-sm font-medium text-golf-gray">{c.full_name}</p>
                                      {c.is_primary && <span className="text-xs bg-golf-orange/10 text-golf-orange px-1.5 py-0.5 rounded-full">Primary</span>}
                                      {c.is_official_contact && (
                                        <span className="inline-flex items-center gap-0.5 text-xs bg-golf-gray/10 text-golf-gray px-1.5 py-0.5 rounded-full">
                                          <ShieldCheckIcon className="w-3 h-3" />Official
                                        </span>
                                      )}
                                    </div>
                                    {c.title && <p className="text-xs text-gray-500">{c.title}</p>}
                                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-gray-500">
                                      {c.email && <a href={`mailto:${c.email}`} className="hover:text-golf-orange flex items-center gap-1"><EnvelopeIcon className="w-3 h-3" />{c.email}</a>}
                                      {c.phone && <a href={`tel:${c.phone}`} className="hover:text-golf-orange flex items-center gap-1"><PhoneIcon className="w-3 h-3" />{c.phone}</a>}
                                    </div>
                                    {(c.street || c.city || c.state) && (
                                      <div className="flex items-start gap-1 mt-1.5 text-xs text-gray-400">
                                        <MapPinIcon className="w-3 h-3 flex-shrink-0 mt-0.5" />
                                        <span>
                                          {[c.street, c.city, c.state, c.postal_code, c.country].filter(Boolean).join(', ')}
                                        </span>
                                      </div>
                                    )}
                                    {c.preferred_contact_method && (
                                      <p className="text-xs text-gray-400 mt-1">Prefers: {c.preferred_contact_method.replace('_', ' ')}</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  <button onClick={() => setContactSheet({ open: true, contact: c })} className="p-1 rounded hover:bg-gray-100 transition-colors">
                                    <PencilIcon className="w-3.5 h-3.5 text-gray-400" />
                                  </button>
                                  {deletingContactId === c.id ? (
                                    <div className="flex gap-1">
                                      <button onClick={() => handleDeleteContact(c.id)} className="text-xs text-red-600 px-2 py-0.5 bg-red-50 rounded">Confirm</button>
                                      <button onClick={() => setDeletingContactId(null)} className="text-xs text-gray-500 px-2 py-0.5 bg-gray-100 rounded">Cancel</button>
                                    </div>
                                  ) : (
                                    <button onClick={() => setDeletingContactId(c.id)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                                      <TrashIcon className="w-3.5 h-3.5 text-gray-400" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </Tab.Panel>

                      <Tab.Panel className="p-5 space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-golf-gray">Tasks ({activeTasks.length} open)</h3>
                          <button onClick={() => setTaskSheet({ open: true })} className="flex items-center gap-1.5 px-3 py-1.5 bg-golf-orange text-white text-xs font-medium rounded-lg hover:bg-golf-orange/90 transition-colors">
                            <PlusIcon className="w-3.5 h-3.5" />
                            Add Task
                          </button>
                        </div>

                        {activeTasks.length === 0 && completedTasks.length === 0 ? (
                          <div className="text-center py-8 text-gray-400">
                            <ClipboardDocumentListIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No tasks yet</p>
                          </div>
                        ) : (
                          <>
                            {activeTasks.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Open</p>
                                {activeTasks.map(task => (
                                  <div key={task.id} className={`flex items-start gap-3 p-3 rounded-xl border ${isOverdue(task.due_date) ? 'border-red-200 bg-red-50' : 'border-gray-200'}`}>
                                    <button onClick={() => handleTaskToggle(task)} className="mt-0.5 flex-shrink-0">
                                      <div className="w-4 h-4 rounded border-2 border-gray-300 hover:border-golf-orange transition-colors" />
                                    </button>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm text-golf-gray font-medium">{task.title}</p>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className={`text-xs px-1.5 py-0.5 rounded-md ${PRIORITY_COLORS[task.priority]}`}>{task.priority}</span>
                                        {task.due_date && (
                                          <span className={`text-xs flex items-center gap-0.5 ${isOverdue(task.due_date) ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                            {isOverdue(task.due_date) && <ExclamationCircleIcon className="w-3 h-3" />}
                                            {new Date(task.due_date).toLocaleDateString()}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <button onClick={() => setTaskSheet({ open: true, task })} className="p-1 rounded hover:bg-gray-200 transition-colors flex-shrink-0">
                                      <PencilIcon className="w-3.5 h-3.5 text-gray-400" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}

                            {completedTasks.length > 0 && (
                              <div className="space-y-2">
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Completed ({completedTasks.length})</p>
                                {completedTasks.slice(0, 5).map(task => (
                                  <div key={task.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 opacity-60">
                                    <button onClick={() => handleTaskToggle(task)} className="mt-0.5 flex-shrink-0">
                                      <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
                                    </button>
                                    <p className="text-sm text-gray-500 line-through">{task.title}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </Tab.Panel>

                      <Tab.Panel className="p-5 space-y-4">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-golf-gray">Commitment</h3>
                          <button onClick={() => setCommitmentSheet({ open: true, commitment: latestCommitment })} className="flex items-center gap-1.5 px-3 py-1.5 bg-golf-orange text-white text-xs font-medium rounded-lg hover:bg-golf-orange/90 transition-colors">
                            {latestCommitment ? <PencilIcon className="w-3.5 h-3.5" /> : <PlusIcon className="w-3.5 h-3.5" />}
                            {latestCommitment ? 'Edit' : 'Add Commitment'}
                          </button>
                        </div>

                        {!latestCommitment ? (
                          <div className="text-center py-8 text-gray-400">
                            <BanknotesIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                            <p className="text-sm">No commitment recorded yet</p>
                          </div>
                        ) : (
                          <div className="border border-gray-200 rounded-xl p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <div>
                                {latestCommitment.tier_interest && (
                                  <p className="text-sm font-semibold text-golf-gray">{latestCommitment.tier_interest}</p>
                                )}
                                {latestCommitment.amount_pledged && (
                                  <p className="text-xl font-bold text-golf-orange">₦{latestCommitment.amount_pledged.toLocaleString()}</p>
                                )}
                              </div>
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${COMMITMENT_STATUS_COLORS[latestCommitment.status]}`}>
                                {latestCommitment.status.charAt(0).toUpperCase() + latestCommitment.status.slice(1)}
                              </span>
                            </div>

                            {latestCommitment.probability_pct !== undefined && (
                              <div>
                                <div className="flex justify-between text-xs text-gray-500 mb-1">
                                  <span>Probability</span>
                                  <span className="font-medium text-golf-gray">{latestCommitment.probability_pct}%</span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-golf-orange rounded-full transition-all"
                                    style={{ width: `${latestCommitment.probability_pct}%` }}
                                  />
                                </div>
                              </div>
                            )}

                            {latestCommitment.expected_close && (
                              <div className="flex items-center gap-2 text-xs text-gray-500">
                                <CalendarDaysIcon className="w-3.5 h-3.5" />
                                Expected close: {new Date(latestCommitment.expected_close).toLocaleDateString()}
                              </div>
                            )}

                            {latestCommitment.notes && (
                              <p className="text-xs text-gray-500 border-t border-gray-100 pt-2">{latestCommitment.notes}</p>
                            )}
                          </div>
                        )}

                        {prospect.prospect_commitments.length > 1 && (
                          <div className="space-y-2">
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Previous Commitments</p>
                            {prospect.prospect_commitments.slice(1).map(c => (
                              <div key={c.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-xs text-gray-500">
                                <span>{c.tier_interest || 'Unspecified'}</span>
                                {c.amount_pledged && <span>₦{c.amount_pledged.toLocaleString()}</span>}
                                <span className={`px-1.5 py-0.5 rounded-full ${COMMITMENT_STATUS_COLORS[c.status]}`}>{c.status}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </Tab.Panel>

                      <Tab.Panel className="p-5 space-y-4">
                        <div>
                          <div className="flex gap-2">
                            <input
                              value={noteText}
                              onChange={e => setNoteText(e.target.value)}
                              onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleAddNote())}
                              placeholder="Add a note..."
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-golf-orange/40 focus:border-golf-orange"
                            />
                            <button onClick={handleAddNote} disabled={!noteText.trim() || addingNote} className="px-3 py-2 bg-golf-orange text-white text-sm rounded-lg hover:bg-golf-orange/90 disabled:opacity-50 transition-colors">
                              Add
                            </button>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {sortedActivities.length === 0 ? (
                            <div className="text-center py-8 text-gray-400">
                              <ChatBubbleLeftIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
                              <p className="text-sm">No activity yet</p>
                            </div>
                          ) : sortedActivities.map((activity, idx) => {
                            const Icon = ACTIVITY_ICONS[activity.activity_type] || ArrowRightCircleIcon;
                            return (
                              <div key={activity.id} className="flex gap-3">
                                <div className="flex flex-col items-center">
                                  <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
                                    <Icon className="w-3.5 h-3.5 text-gray-500" />
                                  </div>
                                  {idx < sortedActivities.length - 1 && (
                                    <div className="w-px flex-1 bg-gray-200 mt-1 min-h-[1rem]" />
                                  )}
                                </div>
                                <div className="flex-1 pb-3">
                                  <div className="flex items-baseline justify-between gap-2">
                                    <p className="text-sm text-golf-gray font-medium">{activity.title}</p>
                                    <span className="text-xs text-gray-400 flex-shrink-0">{timeAgo(activity.created_at)}</span>
                                  </div>
                                  {activity.body && (
                                    <p className="text-sm text-gray-500 mt-0.5">{activity.body}</p>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </Tab.Panel>
                    </Tab.Panels>
                  </Tab.Group>
                </>
              ) : null}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {prospect && (
        <>
          <ProspectTaskSheet
            isOpen={taskSheet.open}
            onClose={() => setTaskSheet({ open: false })}
            onSuccess={() => { fetchProspect(); onRefresh(); }}
            prospectId={prospect.id}
            companyName={prospect.company_name}
            task={taskSheet.task}
          />
          <ProspectContactSheet
            isOpen={contactSheet.open}
            onClose={() => setContactSheet({ open: false })}
            onSuccess={fetchProspect}
            prospectId={prospect.id}
            contact={contactSheet.contact}
          />
          <ProspectCommitmentSheet
            isOpen={commitmentSheet.open}
            onClose={() => setCommitmentSheet({ open: false })}
            onSuccess={fetchProspect}
            prospectId={prospect.id}
            commitment={commitmentSheet.commitment}
          />
          <ProspectActivateModal
            isOpen={activateOpen}
            onClose={() => setActivateOpen(false)}
            onSuccess={() => { fetchProspect(); onRefresh(); }}
            prospect={{ ...prospect, prospect_contacts: prospect.prospect_contacts, prospect_commitments: prospect.prospect_commitments }}
          />
        </>
      )}
    </>
  );
}
